import { defineStore } from "pinia";
import { ref, computed } from "vue";
import axios from "axios";
import { authApi } from "@/api/auth";
import { queryClient } from "@/queryClient";

const VERIFY_CACHE_MS = 60_000;
const EMAIL_KEY = "ob_auth_email";
const ROLE_KEY = "ob_auth_role";
const SESSION_KEY = "ob_auth_session";
/** Legacy XSS-exposed token — clear on load. */
const LEGACY_TOKEN_KEY = "ob_auth_token";

export type UserRole = "super_admin" | "admin";

try {
  localStorage.removeItem(LEGACY_TOKEN_KEY);
} catch {
  /* ignore */
}

export const useAuthStore = defineStore("auth", () => {
  const email = ref<string | null>(localStorage.getItem(EMAIL_KEY));
  const role = ref<UserRole | null>(
    (localStorage.getItem(ROLE_KEY) as UserRole | null) ?? null
  );
  const name = ref<string | null>(null);
  const hasSession = ref(localStorage.getItem(SESSION_KEY) === "1" || !!email.value);
  const isVerifying = ref(false);
  let lastVerifiedAt = 0;
  let inflightVerify: Promise<boolean> | null = null;

  const isAuthenticated = computed(() => hasSession.value);
  const isSuperAdmin = computed(() => role.value === "super_admin");

  function setSession(newEmail: string, newRole?: UserRole, newName?: string) {
    email.value = newEmail;
    hasSession.value = true;
    localStorage.setItem(SESSION_KEY, "1");
    localStorage.setItem(EMAIL_KEY, newEmail);
    if (newRole) {
      role.value = newRole;
      localStorage.setItem(ROLE_KEY, newRole);
    }
    if (newName) name.value = newName;
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    lastVerifiedAt = Date.now();
  }

  function clearSession() {
    lastVerifiedAt = 0;
    email.value = null;
    role.value = null;
    name.value = null;
    hasSession.value = false;
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    queryClient.clear();
  }

  async function verify(): Promise<boolean> {
    if (hasSession.value && Date.now() - lastVerifiedAt < VERIFY_CACHE_MS) return true;
    if (inflightVerify) return inflightVerify;
    inflightVerify = runVerify().finally(() => {
      inflightVerify = null;
    });
    return inflightVerify;
  }

  async function runVerify(): Promise<boolean> {
    isVerifying.value = true;
    try {
      const res = await authApi.verify();
      if (!res.success) {
        clearSession();
        return false;
      }
      if (res.email) {
        email.value = res.email;
        localStorage.setItem(EMAIL_KEY, res.email);
      }
      if (res.role) {
        role.value = res.role;
        localStorage.setItem(ROLE_KEY, res.role);
      }
      if (res.name) name.value = res.name;
      hasSession.value = true;
      localStorage.setItem(SESSION_KEY, "1");
      lastVerifiedAt = Date.now();
      return true;
    } catch (e: unknown) {
      const status = axios.isAxiosError(e) ? e.response?.status : undefined;
      if (status === 401 || status === 403) {
        clearSession();
        return false;
      }
      return hasSession.value;
    } finally {
      isVerifying.value = false;
    }
  }

  async function logout() {
    try {
      await authApi.logout();
    } finally {
      clearSession();
    }
  }

  return {
    email,
    /** @deprecated Token lives in httpOnly cookie only */
    token: computed(() => null as string | null),
    role,
    name,
    isAuthenticated,
    isSuperAdmin,
    isVerifying,
    setSession,
    clearSession,
    verify,
    logout,
  };
});
